import { invalidate } from '@react-three/fiber';
import { useCallback, useEffect, useRef } from 'react';

import { updateAttributeRange } from 'app/modules/wallforming/components/scenes/objects/articles/utils/batchedMeshUtils';
import { usePoiEventHandler } from 'app/modules/wallforming/components/scenes/objects/pois/hooks/usePoiEventHandler';
import { POIFilterType } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiFilterType';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

import type { PoiBatchedEntry } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiBatchedEntry';
import type { PoiSelection } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiSelection';
import type { BatchedMesh, BufferGeometry } from 'three';

interface PoiBatchedMeshHandlerProps {
    entries: PoiBatchedEntry[];
    geometriesByType: Map<POIFilterType, BufferGeometry>;
}

const updatedIndices = new Set<number>();

function isPoiVisible(filterType: POIFilterType | undefined): boolean {
    return filterType !== undefined && !usePlacementStore.getState().hiddenPOIFilterTypes.includes(filterType);
}

export const usePoiBatchedMeshHandler = ({ entries, geometriesByType }: PoiBatchedMeshHandlerProps) => {
    const meshRef = useRef<BatchedMesh | null>(null);
    const previousMeshRef = useRef<BatchedMesh | null>(null);
    const geometryIdsByTypeRef = useRef<Map<POIFilterType, number>>(new Map());
    const instanceIdByEntryIndexRef = useRef<number[]>([]);
    const entryIndexByInstanceIdRef = useRef<Map<number, number>>(new Map());
    const hoveredBatchIdRef = useRef<number | null>(null);
    const poiSelectionRef = useRef<PoiSelection>(usePlacementStore.getState().poiSelection.current);

    const { handleOnPoiSelection, handleOnPointerMove, handleOnPointerLeave } = usePoiEventHandler({
        entries,
        meshRef,
        hoveredBatchIdRef,
        instanceIdByEntryIndexRef,
        entryIndexByInstanceIdRef
    });

    const initGeometries = useCallback(
        (mesh: BatchedMesh) => {
            const map = new Map<POIFilterType, number>();
            for (const [filterType, geometry] of geometriesByType) {
                map.set(filterType, mesh.addGeometry(geometry));
            }
            geometryIdsByTypeRef.current = map;
        },
        [geometriesByType]
    );

    // Synchronize matrices when POI entries change
    const syncEntries = useCallback(
        (mesh: BatchedMesh | null) => {
            if (!mesh) {
                return;
            }

            const instanceIds = instanceIdByEntryIndexRef.current;
            const entryIndices = entryIndexByInstanceIdRef.current;
            for (const [instanceId, entryIndex] of entryIndices) {
                if (entryIndex >= entries.length) {
                    mesh.deleteInstance(instanceId);
                    entryIndices.delete(instanceId);
                }
            }
            instanceIds.length = entries.length;
            hoveredBatchIdRef.current = null;
            entries.forEach((entry, entryIndex) => {
                const geometryId =
                    entry.filterType !== undefined ? geometryIdsByTypeRef.current.get(entry.filterType) : undefined;
                let instanceId = instanceIds[entryIndex];
                if (geometryId === undefined) {
                    if (instanceId !== undefined) {
                        mesh.deleteInstance(instanceId);
                        entryIndices.delete(instanceId);
                        delete instanceIds[entryIndex];
                    }
                    return;
                }
                if (instanceId === undefined) {
                    instanceId = mesh.addInstance(geometryId);
                    instanceIds[entryIndex] = instanceId;
                    entryIndices.set(instanceId, entryIndex);
                } else {
                    mesh.setGeometryIdAt(instanceId, geometryId);
                }
                mesh.setMatrixAt(instanceId, entry.matrix);
                mesh.setColorAt(
                    instanceId,
                    poiSelectionRef.current.index === entry.poiIndex ? entry.selectedColor : entry.defaultColor
                );
                mesh.setVisibleAt(instanceId, isPoiVisible(entry.filterType));
            });
            mesh.boundingBox = null;
            mesh.boundingSphere = null;
            invalidate();
        },
        [entries]
    );

    const handleMeshRef = useCallback(
        (mesh: BatchedMesh | null) => {
            meshRef.current = mesh;
            if (!mesh || mesh === previousMeshRef.current) {
                return;
            }

            previousMeshRef.current = mesh;
            instanceIdByEntryIndexRef.current = [];
            entryIndexByInstanceIdRef.current.clear();
            initGeometries(mesh);
            syncEntries(mesh);
        },
        [initGeometries, syncEntries]
    );

    useEffect(() => {
        const mesh = meshRef.current;
        if (!mesh) {
            return;
        }

        syncEntries(mesh);
    }, [entries, syncEntries]);

    // Update POI hover highlight
    useEffect(() => {
        const subscribedPoiSelection = usePlacementStore.subscribe(
            (state) => state.hiddenPOIFilterTypes,
            (newHiddenPOIFilterTypes) => {
                const mesh = meshRef.current;
                if (!mesh) {
                    return;
                }

                const hiddenTypes = new Set(newHiddenPOIFilterTypes);
                const instanceIdByEntryIndex = instanceIdByEntryIndexRef.current;

                for (let entryIndex = 0; entryIndex < entries.length; entryIndex++) {
                    const instanceId = instanceIdByEntryIndex[entryIndex];
                    if (instanceId === undefined) {
                        continue;
                    }

                    const entry = entries[entryIndex];
                    mesh.setVisibleAt(instanceId, entry.filterType !== undefined && !hiddenTypes.has(entry.filterType));
                }

                invalidate();
            }
        );

        return () => subscribedPoiSelection();
    }, [entries]);

    // Update POI selection highlight
    useEffect(() => {
        const subscribedPoiSelection = usePlacementStore.subscribe(
            (state) => state.poiSelection.current,
            (newPoiSelection) => {
                poiSelectionRef.current = newPoiSelection;

                const mesh = meshRef.current;
                if (!mesh) {
                    return;
                }

                updatedIndices.clear();
                const instanceIdByEntryIndex = instanceIdByEntryIndexRef.current;

                for (let entryIndex = 0; entryIndex < entries.length; entryIndex++) {
                    const instanceId = instanceIdByEntryIndex[entryIndex];
                    if (instanceId === undefined) {
                        continue;
                    }

                    const entry = entries[entryIndex];
                    const isSelected = newPoiSelection.index === entry.poiIndex;
                    const isHovered = hoveredBatchIdRef.current === instanceId;
                    mesh.setColorAt(
                        instanceId,
                        isSelected
                            ? entry.selectedColor
                            : isHovered
                              ? (entry.hoverColor ?? entry.selectedColor)
                              : entry.defaultColor
                    );
                    updatedIndices.add(instanceId);
                }

                updateAttributeRange(mesh, updatedIndices, 'colorAttribute', 4);
                invalidate();
            }
        );

        return () => subscribedPoiSelection();
    }, [entries]);

    // Handle geometry disposal
    useEffect(() => {
        const mesh = meshRef.current;
        return () => {
            mesh?.geometry?.dispose();
        };
    }, []);

    return {
        meshRef,
        handleMeshRef,
        handleOnPoiSelection,
        handleOnPointerMove,
        handleOnPointerLeave,
        hoveredBatchIdRef,
        instanceIdByEntryIndexRef,
        entryIndexByInstanceIdRef
    };
};
